import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_afc4fae4-3e91-49c0-b442-1e22d639f2c6/artifacts/5tm3hua0_Logo%20PF%402x.png";

const PrivacyPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contentRes = await axios.get(`${API}/content/privacy`);
        setContent(contentRes.data);
      } catch (error) {
        console.error('Error fetching privacy content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-inter">Chargement...</p>
        </div>
      </div>
    );
  }

  const extra = content?.extra_fields || {};

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Place Financière BFC" className="h-10" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-[#1e3a8a] hover:text-[#172554] font-inter text-sm transition-colors"
            >
              <ArrowLeft size={16} />
              Retour au site
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-playfair text-4xl font-bold text-[#0F172A] mb-8">
          {content?.title || 'Politique de confidentialité'}
        </h1>

        <div className="prose prose-lg max-w-none font-inter text-slate-600 space-y-8">
          {/* Section 1 - Introduction */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section1_title || '1. Introduction'}
            </h2>
            <p className="whitespace-pre-line">
              {extra.section1_content || `La présente politique de confidentialité a pour objectif d'informer les utilisateurs du site de la Place Financière Bourgogne-Franche-Comté sur la manière dont leurs données personnelles peuvent être collectées, utilisées et protégées lors de leur navigation sur le site. La Place Financière Bourgogne-Franche-Comté s'engage à respecter la réglementation applicable en matière de protection des données personnelles, notamment le Règlement (UE) 2016/679 du 27 avril 2016 (RGPD) ainsi que la loi Informatique et Libertés.`}
            </p>
          </section>

          {/* Section 2 - Responsable du traitement */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section2_title || '2. Responsable du traitement'}
            </h2>
            <div className="whitespace-pre-line">
              {extra.section2_content || `Le responsable du traitement des données est :
Place Financière Bourgogne-Franche-Comté
Association loi 1901
2 avenue de Marbotte
21000 Dijon – France
SIREN : 837 672 914
Adresse e-mail : contact@placefinanciere-bfc.fr`}
            </div>
          </section>

          {/* Section 3 - Données collectées */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section3_title || '3. Données collectées'}
            </h2>
            <p className="whitespace-pre-line">
              {extra.section3_content || `Les données personnelles susceptibles d'être collectées via le site sont uniquement celles que l'utilisateur choisit de transmettre volontairement, notamment via le formulaire de contact. Ces données peuvent inclure :
• nom
• prénom
• adresse e-mail
• numéro de téléphone (si renseigné)
• organisation ou société (si renseignée)
• contenu du message

Aucune donnée sensible n'est collectée via le site.`}
            </p>
          </section>

          {/* Section 4 - Finalités */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section4_title || '4. Finalités de la collecte'}
            </h2>
            <p className="whitespace-pre-line">
              {extra.section4_content || `Les données personnelles collectées sont utilisées uniquement pour les finalités suivantes :
• répondre aux demandes adressées via le formulaire de contact
• assurer le suivi des échanges avec les utilisateurs
• gérer les demandes d'information ou de prise de contact

Ces données ne sont jamais utilisées à des fins commerciales ou publicitaires sans le consentement préalable de l'utilisateur.`}
            </p>
          </section>

          {/* Section 5 - Base légale */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section5_title || '5. Base légale du traitement'}
            </h2>
            <p className="whitespace-pre-line">
              {extra.section5_content || `Le traitement des données repose sur :
• le consentement de l'utilisateur, lorsqu'il remplit volontairement le formulaire de contact
• l'intérêt légitime de la Place Financière Bourgogne-Franche-Comté à répondre aux demandes qui lui sont adressées`}
            </p>
          </section>

          {/* Section 6 - Destinataires */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section6_title || '6. Destinataires des données'}
            </h2>
            <p className="whitespace-pre-line">
              {extra.section6_content || `Les données collectées sont destinées exclusivement à la Place Financière Bourgogne-Franche-Comté et aux personnes habilitées à traiter les demandes reçues via le site. Elles ne sont ni vendues, ni cédées, ni louées à des tiers. Dans certains cas, les données peuvent être traitées par des prestataires techniques (hébergement du site, maintenance informatique) agissant uniquement sur instruction de la Place Financière Bourgogne-Franche-Comté et dans le respect du RGPD.`}
            </p>
          </section>

          {/* Section 7 - Durée de conservation */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section7_title || '7. Durée de conservation'}
            </h2>
            <p>
              {extra.section7_content || `Les données personnelles sont conservées uniquement pendant la durée nécessaire au traitement des demandes. Sauf obligation légale contraire, les données issues du formulaire de contact sont conservées pour une durée maximale de 3 ans à compter du dernier échange.`}
            </p>
          </section>

          {/* Section 8 - Sécurité */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section8_title || '8. Sécurité des données'}
            </h2>
            <p>
              {extra.section8_content || `La Place Financière Bourgogne-Franche-Comté met en œuvre les mesures techniques et organisationnelles appropriées afin de garantir la sécurité et la confidentialité des données personnelles. Ces mesures visent notamment à empêcher toute perte, altération, divulgation ou accès non autorisé aux données.`}
            </p>
          </section>

          {/* Section 9 - Droits */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section9_title || '9. Droits des utilisateurs'}
            </h2>
            <p className="whitespace-pre-line">
              {extra.section9_content || `Conformément à la réglementation applicable, les utilisateurs disposent des droits suivants :
• droit d'accès aux données personnelles
• droit de rectification des données inexactes
• droit d'effacement des données
• droit d'opposition au traitement
• droit à la limitation du traitement
• droit à la portabilité des données

Pour exercer ces droits, l'utilisateur peut adresser une demande à : contact@placefinanciere-bfc.fr
La demande devra être accompagnée d'un justificatif d'identité si nécessaire.`}
            </p>
          </section>

          {/* Section 10 - CNIL */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section10_title || '10. Réclamation auprès de la CNIL'}
            </h2>
            <div className="whitespace-pre-line">
              {extra.section10_content || `Si un utilisateur estime que ses droits ne sont pas respectés, il peut introduire une réclamation auprès de l'autorité de contrôle compétente :
CNIL – Commission Nationale de l'Informatique et des Libertés
3 Place de Fontenoy
75007 Paris
www.cnil.fr`}
            </div>
          </section>

          {/* Section 11 - Cookies */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section11_title || '11. Cookies'}
            </h2>
            <p>
              {extra.section11_content || `Le site peut utiliser des cookies ou technologies similaires afin d'améliorer l'expérience de navigation et d'établir des statistiques de fréquentation. Un cookie est un petit fichier enregistré sur l'appareil de l'utilisateur lors de sa navigation. L'utilisateur peut à tout moment configurer son navigateur pour refuser tout ou partie des cookies.`}
            </p>
          </section>

          {/* Section 12 - Modification */}
          <section>
            <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-4">
              {extra.section12_title || '12. Modification de la politique de confidentialité'}
            </h2>
            <p>
              {extra.section12_content || `La Place Financière Bourgogne-Franche-Comté se réserve le droit de modifier la présente politique de confidentialité à tout moment afin de garantir sa conformité avec la législation en vigueur. La version applicable est celle publiée sur le site à la date de consultation.`}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500 font-inter">
            © {new Date().getFullYear()} Place Financière Bourgogne-Franche-Comté. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
